import React, { useRef, useState } from 'react';
import {
    Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
    FEEDBACK_ATTACHMENT_MAX, FEEDBACK_ATTACHMENT_MAX_BYTES, FEEDBACK_ATTACHMENT_TYPES, FeedbackService, FeedbackType,
} from '../../services/feedback.service';
import { fileSize } from './FeedbackAttachmentChips';

export const FEEDBACK_MAX = 2000;

export const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
    { value: 'BUG', label: 'Bug' },
    { value: 'IDEA', label: 'Idea' },
    { value: 'QUESTION', label: 'Question' },
];

/** The running build, e.g. "main.3603930b", read from the bundle's script name; blank in development. */
export function appVersion(): string {
    const script = Array.from(document.scripts).map(s => s.src).find(src => /\/main\.[0-9a-f]+\.js/.test(src));
    return script?.match(/main\.[0-9a-f]+/)?.[0] ?? (process.env.REACT_APP_VERSION || '');
}

interface Props {
    open: boolean;
    onClose: () => void;
}

/**
 * Bug / idea / question to the Kervolt team. The page, roles, institution, build and browser go along
 * automatically so the user only writes the message.
 */
const FeedbackDialog: React.FC<Props> = ({ open, onClose }) => {
    const toast = useToast();
    const location = useLocation();
    const { roles, user, selectedInstitutionId } = useAuth();
    const [type, setType] = useState<FeedbackType>('BUG');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const fileInput = useRef<HTMLInputElement>(null);

    const trimmed = message.trim();
    const tooLong = message.length > FEEDBACK_MAX;

    const close = () => {
        if (sending) return;
        onClose();
    };

    const roleNames = () => {
        const names = (roles ?? []).map(r => r.name).filter(Boolean);
        // Platform staff have no institution roles; their JWT role string (e.g. "SaaSOperator") is used instead.
        return (names.length ? names.join(', ') : String(user?.roles ?? '')).slice(0, 255);
    };

    const pickFiles = (chosen: File[]) => {
        if (fileInput.current) fileInput.current.value = '';
        const wrongType = chosen.filter(f => !FEEDBACK_ATTACHMENT_TYPES.includes(f.type));
        const tooBig = chosen.filter(f => f.size > FEEDBACK_ATTACHMENT_MAX_BYTES);
        if (wrongType.length) toast.showWarning(`Only PNG, JPEG or PDF files can be attached (${wrongType.map(f => f.name).join(', ')}).`);
        if (tooBig.length) toast.showWarning(`Each file can be up to 5 MB (${tooBig.map(f => f.name).join(', ')}).`);
        const ok = chosen.filter(f => !wrongType.includes(f) && !tooBig.includes(f));
        const next = [...files, ...ok];
        if (next.length > FEEDBACK_ATTACHMENT_MAX) toast.showWarning(`You can attach up to ${FEEDBACK_ATTACHMENT_MAX} files.`);
        setFiles(next.slice(0, FEEDBACK_ATTACHMENT_MAX));
    };

    /**
     * Ctrl+V: copied text goes into the message as usual; a copied screenshot or file (nothing but files on the
     * clipboard) becomes an attachment. Each paste adds one more, up to the limit.
     */
    const pasteFiles = (e: React.ClipboardEvent) => {
        if (e.clipboardData.getData('text/plain')) return;
        const pasted = Array.from(e.clipboardData.items ?? [])
            .filter(item => item.kind === 'file')
            .map(item => item.getAsFile())
            .filter((f): f is File => !!f);
        if (!pasted.length) return;
        e.preventDefault();
        if (sending) return;
        const taken = new Set(files.map(f => f.name));
        const stamp = new Date().toTimeString().slice(0, 8).replace(/:/g, '');
        pickFiles(pasted.map(f => {
            // Screenshots arrive as a generic "image.png"; give them a readable name.
            const base = !f.name || /^image\.(png|jpe?g)$/i.test(f.name)
                ? `screenshot-${stamp}.${f.type === 'image/jpeg' ? 'jpg' : 'png'}`
                : f.name;
            let name = base;
            for (let n = 2; taken.has(name); n++) name = base.replace(/(\.[^.]+)?$/, ext => `-${n}${ext}`);
            taken.add(name);
            return name === f.name ? f : new File([f], name, { type: f.type });
        }));
    };

    const send = async () => {
        setSending(true);
        try {
            const saved = await FeedbackService.send({
                type,
                message: trimmed,
                institutionId: selectedInstitutionId,
                pagePath: location.pathname.slice(0, 500),
                roleNames: roleNames(),
                appVersion: appVersion().slice(0, 64),
                userAgent: navigator.userAgent.slice(0, 500),
            });
            let filesFailed = false;
            if (files.length) {
                try {
                    await FeedbackService.uploadAttachments(saved.id, files);
                } catch {
                    filesFailed = true;
                }
            }
            if (filesFailed) {
                toast.showWarning('Your feedback was sent, but the files could not be attached. Please send them again in a new message.');
            } else {
                toast.showSuccess('Thank you. Your feedback was sent to the Kervolt team.');
            }
            setMessage('');
            setType('BUG');
            setFiles([]);
            onClose();
        } catch (error) {
            if ((error as { response?: { status?: number } })?.response?.status === 429) {
                toast.showWarning('You have sent a lot of feedback in the last hour. Please try again later.');
            } else {
                toast.showApiError(error, 'Failed to send feedback');
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm" data-help-page="feedback.dialog" onPaste={pasteFiles}>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogContent>
                <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 0.5, flexWrap: 'wrap' }} data-help="feedback.dialog.type">
                    {FEEDBACK_TYPES.map(t => (
                        <Chip
                            key={t.value}
                            label={t.label}
                            color={type === t.value ? 'primary' : 'default'}
                            variant={type === t.value ? 'filled' : 'outlined'}
                            onClick={() => setType(t.value)}
                            aria-pressed={type === t.value}
                        />
                    ))}
                </Stack>
                <TextField
                    label="Your message"
                    data-help="feedback.dialog.message"
                    multiline
                    minRows={4}
                    fullWidth
                    autoFocus
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    error={tooLong}
                    helperText={`${message.length} / ${FEEDBACK_MAX}. Please don't paste passwords.`}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AttachFileIcon />}
                        onClick={() => fileInput.current?.click()}
                        disabled={sending || files.length >= FEEDBACK_ATTACHMENT_MAX}
                        data-help="feedback.dialog.attach"
                    >
                        Attach files
                    </Button>
                    <input
                        ref={fileInput}
                        type="file"
                        hidden
                        multiple
                        accept={FEEDBACK_ATTACHMENT_TYPES.join(',')}
                        data-testid="feedback-file-input"
                        onChange={e => pickFiles(Array.from(e.target.files ?? []))}
                    />
                    {files.map((f, i) => (
                        <Chip
                            key={`${f.name}-${i}`}
                            size="small"
                            label={`${f.name} (${fileSize(f.size)})`}
                            onDelete={sending ? undefined : () => setFiles(files.filter((_, j) => j !== i))}
                            sx={{ maxWidth: 260 }}
                        />
                    ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Up to {FEEDBACK_ATTACHMENT_MAX} screenshots or PDFs, 5 MB each. You can also paste a screenshot (Ctrl+V). The page you are on and your role are sent with the message. You can read the reply under My Profile → My feedback.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={sending}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={send}
                    disabled={sending || !trimmed || tooLong}
                    data-help="feedback.dialog.send"
                >
                    {sending ? 'Sending…' : 'Send'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;
